/**
 * Rider Store - Zustand
 * 
 * Manages:
 * - Rider status (offline/available/busy)
 * - Zone selection
 * - Current location
 * - Statistics
 * - Sprint 28: Profile info (name, homeZone, coverageZones, accountStatus)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '@/lib/logger';

export type RiderStatus = 'offline' | 'available' | 'busy';
export type AccountStatus = 'pending' | 'active' | 'suspended' | 'disabled';

export interface Zone {
  id: string;
  name: string;
  polygon?: Array<{ lat: number; lng: number }>;
  isActive?: boolean;
}

export interface RiderStats {
  todayEarnings: number;
  todayDeliveries: number;
  weeklyEarnings: number;
  weeklyDeliveries: number;
  rating: number;
  totalDeliveries: number;
}

// Sprint 28: Rider profile from backend
export interface RiderProfile {
  id: string;
  userId: string;
  name: string;
  phone: string;
  accountStatus: AccountStatus;
  homeZone: Zone | null;
  coverageZones: Zone[];
  vehicleType?: string;
  plateNumber?: string;
  inCooldown: boolean;
}

interface RiderState {
  // Status
  status: RiderStatus;
  
  // Sprint 28: Profile
  profile: RiderProfile | null;
  isLoadingProfile: boolean;
  
  // Zone (legacy - keep for backward compat)
  selectedZone: Zone | null;
  availableZones: Zone[];
  
  // Location
  currentLocation: {
    lat: number;
    lng: number;
    accuracy: number;
    bearing?: number; // Heading/direction in degrees (0-360)
  } | null;
  
  // Stats
  stats: RiderStats;
  
  // Network
  isOnline: boolean;
  
  // Actions
  setStatus: (status: RiderStatus) => void;
  setProfile: (profile: RiderProfile | null) => void;
  setSelectedZone: (zone: Zone | null) => void;
  setAvailableZones: (zones: Zone[]) => void;
  setCurrentLocation: (location: { lat: number; lng: number; accuracy: number; bearing?: number }) => void;
  setStats: (stats: Partial<RiderStats>) => void;
  setIsOnline: (online: boolean) => void;
  
  // Toggle actions
  goOnline: () => void;
  goOffline: () => void;
  setBusy: () => void;
  setAvailable: () => void;
  
  // Reset
  resetRider: () => void;
  
  // Initialize
  initializeRider: () => Promise<void>;
  
  // Sprint 28: Computed helpers
  canGoOnline: () => boolean;
  getBlockReason: () => string | null;
}

const initialStats: RiderStats = {
  todayEarnings: 0,
  todayDeliveries: 0,
  weeklyEarnings: 0,
  weeklyDeliveries: 0,
  rating: 5.0,
  totalDeliveries: 0,
};

export const useRiderStore = create<RiderState>()(
  persist(
    (set, get) => ({
      status: 'offline',
      profile: null,
      isLoadingProfile: false,
      selectedZone: null,
      availableZones: [],
      currentLocation: null,
      stats: initialStats,
      isOnline: navigator.onLine,

      setStatus: (status) => {
        logger.info('Rider status changed', { status });
        set({ status });
      },

      setProfile: (profile) => {
        logger.info('Rider profile updated', { 
          riderId: profile?.id, 
          accountStatus: profile?.accountStatus,
          homeZone: profile?.homeZone?.name 
        });
        set({ profile });
        
        // Also update selectedZone for backward compatibility
        if (profile?.homeZone) {
          set({ selectedZone: profile.homeZone });
        }
      },

      setSelectedZone: (zone) => {
        logger.info('Zone selected', { zoneId: zone?.id, zoneName: zone?.name });
        set({ selectedZone: zone });
      },

      setAvailableZones: (zones) => {
        set({ availableZones: zones });
      },

      setCurrentLocation: (location) => {
        set({ currentLocation: location });
      },

      setStats: (newStats) => {
        const { stats } = get();
        set({ stats: { ...stats, ...newStats } });
      },

      setIsOnline: (online) => {
        logger.info('Network status changed', { online });
        set({ isOnline: online });
      },

      goOnline: async () => {
        const { selectedZone, currentLocation } = get();
        
        if (!selectedZone) {
          logger.warn('Cannot go online - no zone selected');
          return;
        }

        try {
          // Use current location or fallback to zone center
          const lat = currentLocation?.lat || 13.155; // Daraga default
          const lng = currentLocation?.lng || 123.7412;

          logger.info('Rider going online - calling API', { zoneId: selectedZone.id, lat, lng });
          
          // Dynamically import API to avoid circular dependencies
          const { riderApi } = await import('../lib/api');
          await riderApi.goOnline(lat, lng);
          
          set({ status: 'available' });
          logger.info('Rider successfully went online');
        } catch (error: any) {
          logger.error('Failed to go online', error);
          // Revert to offline on error
          set({ status: 'offline' });
          throw error;
        }
      },

      goOffline: async () => {
        try {
          logger.info('Rider going offline - calling API');
          
          // Dynamically import API to avoid circular dependencies
          const { riderApi } = await import('../lib/api');
          await riderApi.goOffline();
          
          set({ status: 'offline' });
          logger.info('Rider successfully went offline');
        } catch (error: any) {
          logger.error('Failed to go offline', error);
          // Still update local state even if API fails
          set({ status: 'offline' });
        }
      },

      setBusy: () => {
        logger.info('Rider status set to busy');
        set({ status: 'busy' });
      },

      setAvailable: () => {
        logger.info('Rider status set to available');
        set({ status: 'available' });
      },

      resetRider: () => {
        logger.info('Rider state reset');
        set({
          status: 'offline',
          selectedZone: null,
          currentLocation: null,
          stats: initialStats,
        });
      },

      initializeRider: async () => {
        try {
          logger.info('Initializing rider profile...');
          set({ isLoadingProfile: true });
          
          // Dynamically import API to avoid circular dependencies
          const { riderApi, zonesApi } = await import('../lib/api');
          
          // Fetch zones first (no auth required)
          const zones = await zonesApi.getAll();
          set({ availableZones: zones });
          logger.info('Available zones loaded', { count: zones.length });
          
          // Try to fetch rider profile (requires correct role)
          try {
            const profileData = await riderApi.getProfile();
            logger.info('Rider profile loaded', { 
              riderId: profileData.id, 
              homeZoneId: profileData.home_zone_id,
              status: profileData.status 
            });
            
            // Build Zone objects from IDs
            const homeZone = profileData.home_zone_id 
              ? zones.find((z: Zone) => z.id === profileData.home_zone_id) || null
              : null;
            
            const coverageZones = (profileData.coverage_zone_ids || [])
              .map((id: string) => zones.find((z: Zone) => z.id === id))
              .filter(Boolean) as Zone[];
            
            // Create profile object
            const profile: RiderProfile = {
              id: profileData.id,
              userId: profileData.user_id,
              name: profileData.name || 'Rider',
              phone: profileData.phone || '',
              accountStatus: (profileData.status as AccountStatus) || 'active',
              homeZone,
              coverageZones,
              vehicleType: profileData.vehicle_type,
              plateNumber: profileData.plate_number,
              inCooldown: profileData.inCooldown || false,
            };
            
            set({ profile, selectedZone: homeZone });
            logger.info('Rider profile initialized', { 
              riderId: profile.id, 
              accountStatus: profile.accountStatus,
              homeZone: profile.homeZone?.name,
              coverageCount: profile.coverageZones.length
            });
          } catch (profileError: any) {
            // If profile fetch fails (403), try to find and select a zone manually
            if (profileError.response?.status === 403) {
              logger.warn('Profile fetch failed (403), attempting manual zone selection');
              
              // Select the first available zone as fallback
              if (zones.length > 0) {
                set({ selectedZone: zones[0] });
                logger.info('Selected first available zone as fallback', { 
                  zoneId: zones[0].id,
                  zoneName: zones[0].name 
                });
              }
            } else {
              throw profileError;
            }
          }
        } catch (error) {
          logger.error('Failed to initialize rider', error as Error);
        } finally {
          set({ isLoadingProfile: false });
        }
      },

      // Sprint 28: Check if rider can go online
      canGoOnline: () => {
        const { profile, selectedZone, isOnline } = get();
        
        // Must have internet
        if (!isOnline) return false;
        
        // Must have a zone selected
        if (!selectedZone) return false;
        
        // If we have a profile, check account status
        if (profile) {
          if (profile.accountStatus === 'suspended') return false;
          if (profile.accountStatus === 'pending') return false;
          if (profile.accountStatus === 'disabled') return false;
          
          // Check if home zone is active
          if (profile.homeZone && profile.homeZone.isActive === false) return false;
        }
        
        return true;
      },

      // Sprint 28: Get reason why rider can't go online
      getBlockReason: () => {
        const { profile, selectedZone, isOnline } = get();
        
        if (!isOnline) return 'No internet connection';
        if (!selectedZone) return 'No zone selected';
        
        if (profile) {
          if (profile.accountStatus === 'suspended') return 'Account suspended';
          if (profile.accountStatus === 'pending') return 'Account pending approval';
          if (profile.accountStatus === 'disabled') return 'Account disabled';
          if (profile.homeZone && profile.homeZone.isActive === false) return 'Zone is currently inactive';
        }
        
        return null;
      },
    }),
    {
      name: 'rider-status-storage',
      partialize: (state) => ({
        status: state.status,
        selectedZone: state.selectedZone,
        stats: state.stats,
        profile: state.profile,
      }),
    }
  )
);








